import moment from 'moment'
import type { SessionStats } from '@/types'

export type AveragePeriod = 'all' | '7d' | '30d' | '90d' | '1y' | 'ytd'

export const AVERAGE_PERIOD_OPTIONS: {
  id: AveragePeriod
  label: string
  short: string
  /** How the comparison window is described, or null when there is none (all-time) */
  previousLabel: string | null
}[] = [
  { id: 'all', label: 'All Time', short: 'All', previousLabel: null },
  { id: '7d', label: 'Last 7 Days', short: '7d', previousLabel: 'prior 7 days' },
  { id: '30d', label: 'Last 30 Days', short: '30d', previousLabel: 'prior 30 days' },
  { id: '90d', label: 'Last 90 Days', short: '90d', previousLabel: 'prior 90 days' },
  { id: '1y', label: 'Last Year', short: '1y', previousLabel: 'prior year' },
  { id: 'ytd', label: 'YTD', short: 'YTD', previousLabel: 'same period last year' },
]

export const AVERAGE_PERIOD_STORAGE_KEY = 'ssdl:stats-average-period'

/** Used until the user picks a period. Week-on-week keeps the feedback loop short. */
export const DEFAULT_AVERAGE_PERIOD: AveragePeriod = '7d'

export function isAveragePeriod(value: unknown): value is AveragePeriod {
  return AVERAGE_PERIOD_OPTIONS.some(o => o.id === value)
}

/** Start of the window for a period, or null for all-time. */
export function averagePeriodCutoff(period: AveragePeriod, now: moment.Moment = moment()): moment.Moment | null {
  switch (period) {
    case '7d': return now.clone().subtract(7, 'days')
    case '30d': return now.clone().subtract(30, 'days')
    case '90d': return now.clone().subtract(90, 'days')
    case '1y': return now.clone().subtract(1, 'year')
    case 'ytd': return now.clone().startOf('year')
    default: return null
  }
}

/**
 * The window immediately before the period, of equal length: e.g. 60–30 days
 * ago for '30d'. YTD compares against Jan 1 → today's date of last year.
 * Null for all-time (nothing to compare against). End is exclusive.
 */
export function previousPeriodWindow(
  period: AveragePeriod,
  now: moment.Moment = moment()
): { start: moment.Moment; end: moment.Moment } | null {
  switch (period) {
    case '7d': return { start: now.clone().subtract(14, 'days'), end: now.clone().subtract(7, 'days') }
    case '30d': return { start: now.clone().subtract(60, 'days'), end: now.clone().subtract(30, 'days') }
    case '90d': return { start: now.clone().subtract(180, 'days'), end: now.clone().subtract(90, 'days') }
    case '1y': return { start: now.clone().subtract(2, 'years'), end: now.clone().subtract(1, 'year') }
    case 'ytd': {
      const lastYear = now.clone().subtract(1, 'year')
      return { start: lastYear.clone().startOf('year'), end: lastYear }
    }
    default: return null
  }
}

export interface AverageStats {
  sessionCount: number
  avgDrinks: number
  avgPeakBac: number
  avgMl: number
  avgCalories: number
}

export interface PeriodTotals {
  sessionCount: number
  drinks: number
  ml: number
  calories: number
}

const finite = (n: number) => (Number.isFinite(n) ? n : 0)

const sumOf = (sessions: SessionStats[], pick: (s: SessionStats) => number) =>
  sessions.reduce((acc, s) => acc + finite(pick(s)), 0)

function averageOf(sessions: SessionStats[]): AverageStats | null {
  if (sessions.length === 0) return null

  const n = sessions.length
  return {
    sessionCount: n,
    avgDrinks: sumOf(sessions, s => s.drinkCount) / n,
    avgPeakBac: sumOf(sessions, s => s.peakBac) / n,
    avgMl: sumOf(sessions, s => s.totalMl) / n,
    avgCalories: sumOf(sessions, s => s.totalCalories) / n,
  }
}

function totalsOf(sessions: SessionStats[]): PeriodTotals {
  return {
    sessionCount: sessions.length,
    drinks: sumOf(sessions, s => s.drinkCount),
    ml: sumOf(sessions, s => s.totalMl),
    calories: sumOf(sessions, s => s.totalCalories),
  }
}

const sessionStart = (s: SessionStats) => moment(s.drinks[0].finished_at)

/** Sessions whose first drink falls within the period (all of them for all-time). */
export function sessionsInPeriod(
  sessions: SessionStats[],
  period: AveragePeriod,
  now: moment.Moment = moment()
): SessionStats[] {
  const cutoff = averagePeriodCutoff(period, now)
  if (!cutoff) return sessions
  return sessions.filter(s => s.drinks.length > 0 && !sessionStart(s).isBefore(cutoff))
}

/** Sessions whose first drink falls within the comparison window, or null for all-time. */
export function sessionsInPreviousPeriod(
  sessions: SessionStats[],
  period: AveragePeriod,
  now: moment.Moment = moment()
): SessionStats[] | null {
  const window = previousPeriodWindow(period, now)
  if (!window) return null
  return sessions.filter(s => {
    if (s.drinks.length === 0) return false
    const start = sessionStart(s)
    return !start.isBefore(window.start) && start.isBefore(window.end)
  })
}

/**
 * Per-session averages over sessions that started within the period.
 * The in-progress session (excludeLast) is left out so a half-finished
 * session doesn't drag the averages down.
 */
export function computeAverageStats(
  sessions: SessionStats[],
  period: AveragePeriod,
  { excludeLast = false, now = moment() }: { excludeLast?: boolean; now?: moment.Moment } = {}
): AverageStats | null {
  const pool = excludeLast ? sessions.slice(0, -1) : sessions
  return averageOf(sessionsInPeriod(pool, period, now))
}

/** Per-session averages over the comparison window (see previousPeriodWindow). */
export function computePreviousAverageStats(
  sessions: SessionStats[],
  period: AveragePeriod,
  { now = moment() }: { now?: moment.Moment } = {}
): AverageStats | null {
  const previous = sessionsInPreviousPeriod(sessions, period, now)
  return previous ? averageOf(previous) : null
}

/**
 * Summed totals over sessions that started within the period. Unlike the
 * averages, the in-progress session is included — its drinks really happened.
 */
export function computePeriodTotals(
  sessions: SessionStats[],
  period: AveragePeriod,
  { now = moment() }: { now?: moment.Moment } = {}
): PeriodTotals {
  return totalsOf(sessionsInPeriod(sessions, period, now))
}

/** Summed totals over the comparison window; null for all-time or an empty window. */
export function computePreviousPeriodTotals(
  sessions: SessionStats[],
  period: AveragePeriod,
  { now = moment() }: { now?: moment.Moment } = {}
): PeriodTotals | null {
  const previous = sessionsInPreviousPeriod(sessions, period, now)
  return previous && previous.length > 0 ? totalsOf(previous) : null
}
